// League Table — the first renderer ported to React (Ben's "tables first").
// A faithful 1:1 port of js/renderers-leaguetable.js: same DOM/classes (the CSS is
// the design), same competition/season pickers + Results switch + row-click-to-Squad,
// same data (via the Phase-02 useLeagueTable hook instead of window.CatalystData).
//
// The `#` rank column that window.initSortableTable injects (a static col-rank of
// 1..N in the default order) is rendered directly in JSX below, so the static view
// matches pixel-for-pixel.
//
// Interactive column-sorting (04g-2): via the shared useSortableRows hook (the React
// port of table-sort.js) — click a header to re-sort with the FLIP slide, sort-asc/desc
// indicators and rank renumber; click `#` to restore. The Team column is no-sort (the
// legacy `.no-sort` opt-out). The default render is points-desc.

import type { ProtoFixture } from "@catbento/shared";
import { useLiveQuery } from "../live/query.js";
import { liveApi } from "../live/api.js";
import { loadSeasonResults } from "../live/results.js";
import { leagueSelectGroups } from "./CountryFlag.js";
import { ModuleExplainer } from "./ModuleExplainer.js";
import { explainLeagueTable } from "./explain.js";
import { LIVE } from "../live/flag.js";
import { useEffect, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { ProtoLeagueStanding } from "@catbento/shared";
import { type ProtoModuleProps, getModState, setModState, switchModuleType } from "./bridge.js";
import { SubControls } from "./SubControls.js";
import { StyledSelect } from "./StyledSelect.js";
import { useLeagueTableCompetitions, useLeagueTable } from "../hooks/useLeagueTable.js";
import { useMatchResultCompetitions } from "../hooks/useMatchResults.js";
import { competitionIdOf, type CompetitionId } from "../data/league.js";
import { useStickyClub } from "../hooks/useStickyClub.js";
import { useFollowedCompetition } from "../hooks/useFollowedCompetition.js";
import { competitionLabel, humanizeSeasonCode, teamForm } from "./league-table-view.js";
import { FormDot } from "./FormDot.js";
import { type SortColumn } from "./sortable-rows.js";
import { useSortableRows } from "./useSortableRows.js";
import { startTeamTokenDragOnMove } from "../canvas/menu-drag.js";

// Module-level (stable identity) so the sort hook doesn't re-sort every render. Order
// matches the header cells below; Team is no-sort (matches the legacy). GA + L default
// to ascending via SORT_LOW_FIRST in the shared core.
const LEAGUE_COLUMNS: SortColumn<ProtoLeagueStanding>[] = [
  { label: "Team", get: (t) => t.name, sortable: false },
  { label: "Played", get: (t) => t.played },
  { label: "W", get: (t) => t.won },
  { label: "D", get: (t) => t.drawn },
  { label: "L", get: (t) => t.lost },
  { label: "GF", get: (t) => t.goalsFor },
  { label: "GA", get: (t) => t.goalsAgainst },
  { label: "GD", get: (t) => t.goalDifference },
  { label: "Pts", get: (t) => t.points },
];

const EMPTY_ROWS: ProtoLeagueStanding[] = [];

const NO_FIXTURES: ProtoFixture[] = [];

export function LeagueTableModule({ mod, body }: ProtoModuleProps) {
  const competitions = useLeagueTableCompetitions();
  const stickyClub = useStickyClub();
  // ?live=1: a fresh table opens on the player's league; a selected one follows a linked pick.
  const opening = useFollowedCompetition(mod, "_leagueTableCompetition", competitions, stickyClub?.competitionId ?? null);
  // The league's id (ids-2) — the `_`-state, Undo and the Results / Squad switches carry it.
  const [competition, setCompetition] = useState<CompetitionId | null>(opening.competition);
  // null → the hook resolves the competition's newest captured season (always, after
  // following a player: the old season belongs to the old league).
  const [season, setSeason] = useState<string | null>(() =>
    opening.followed ? null : (getModState<string>(mod, "_leagueTableSeason") ?? null),
  );

  // ?live=1: a team dropped on the table, its row highlighted (teams and matches, Ben 2026-10-30);
  // after following a player (dropped on it, or a linked pick), their club's. Kept through a
  // season change (the same club); a new league drops it.
  const [highlight, setHighlight] = useState<number | null>(() =>
    opening.followed ? (stickyClub?.teamId ?? null) : (getModState<number>(mod, "_leagueTableHighlight") ?? null),
  );
  const table = useLeagueTable(competition, season ?? undefined);
  // Live: the season's results, for the form column (Ben, 2026-10-18 — Catalyst's table has none).
  const fixtures =
    useLiveQuery(LIVE && competition !== null && table?.seasonCode ? `results:season:${competition}:${table.seasonCode}` : null, () =>
      loadSeasonResults(liveApi(), competition as number, table?.seasonCode as string),
    ) ?? NO_FIXTURES;
  const resultsCompetitions = useMatchResultCompetitions();

  // Sort hook — called unconditionally (before the early returns). EMPTY_ROWS keeps a
  // stable identity when there's no table yet, so the hook doesn't churn.
  const { rows: sortedTable, headerProps, rankProps, containerRef } = useSortableRows(
    table?.table ?? EMPTY_ROWS,
    LEAGUE_COLUMNS,
  );

  // Body is centered by default (for the empty state); the table wants top-align,
  // matching the legacy `body.style.justifyContent = 'flex-start'`.
  useEffect(() => {
    body.style.justifyContent = competitions.length ? "flex-start" : "";
  }, [body, competitions.length]);

  // Write the resolved selection back onto the module element as `_`-state, so
  // main.js's saveState()/undo + presets capture it (the instance-state contract).
  useEffect(() => {
    if (competition !== null) setModState(mod, "_leagueTableCompetition", competition);
    if (table?.seasonCode) setModState(mod, "_leagueTableSeason", table.seasonCode);
    setModState(mod, "_leagueTableHighlight", highlight ?? undefined);
  }, [mod, competition, table?.seasonCode, highlight]);

  if (!competitions.length) {
    return <div className="stats-empty">No league table data captured.</div>;
  }

  // Picks go onto the `_`-state AT ONCE, not only via the effect above: with ?live=1 the
  // new table may still be loading — or fail — when the effect would have run, and Retry
  // rebuilds the widget from its `_`-state, so it must retry the pick, not the old view.
  // (On the snapshot site the effect writes the same values a moment later — no change.)
  const onCompetition = (value: string) => {
    const next = competitionIdOf(value);
    setModState(mod, "_leagueTableCompetition", next);
    setModState(mod, "_leagueTableSeason", undefined);
    setModState(mod, "_leagueTableHighlight", undefined);
    setCompetition(next);
    setHighlight(null);
    setSeason(null); // a competition can't keep another's season — fall back to its newest
  };
  const onSeason = (next: string) => {
    setModState(mod, "_leagueTableSeason", next);
    setSeason(next);
  };
  const onResults = () => {
    setModState(mod, "_matchResultsCompetition", competition);
    setModState(mod, "_matchResultsTeam", undefined);
    switchModuleType(mod, "Match Results");
  };
  // The club by id on ?live=1 (real rows carry one, B1); by name on the snapshot, which has none.
  const onRowClick = (team: ProtoLeagueStanding) => {
    setModState(mod, "_squadsCompetition", competition);
    setModState(mod, "_squadsTeam", team.teamId ?? team.name);
    setModState(mod, "_squadsReturnTitle", "League Table");
    switchModuleType(mod, "Squad");
  };
  // ?live=1: a row is also a team you can pick up (teams and matches, Ben 2026-10-30) — onto empty
  // canvas for its team card, onto a Bio to show it there. A plain click still opens the squad.
  const onRowPress = (e: ReactPointerEvent<HTMLDivElement>, team: ProtoLeagueStanding) => {
    if (!LIVE || team.teamId === undefined || competition === null || !table) return;
    if (window.innerWidth <= 768 || e.button !== 0) return;
    e.stopPropagation();
    startTeamTokenDragOnMove(e.nativeEvent, { id: team.teamId, name: team.name, competitionId: competition, season: table.seasonCode });
  };

  const subControls = (
    <SubControls mod={mod} body={body}>
      <StyledSelect
        // Live: grouped and searchable — top leagues, then by country, with flags (Ben, 2026-10-18).
        items={LIVE ? leagueSelectGroups(competitions, competition) : competitions.map((c) => ({ value: String(c.id), label: c.label }))}
        value={competition === null ? "" : String(competition)}
        onChange={onCompetition}
        wrapClass="league-table-competition-select-wrap"
        extraClass="league-table-competition-select"
        searchable={LIVE}
      />
      <StyledSelect
        items={(table?.seasonCodes ?? []).map((s) => ({ value: s, label: humanizeSeasonCode(s) }))}
        value={table?.seasonCode ?? ""}
        onChange={onSeason}
        wrapClass="league-table-season-select-wrap"
        extraClass="league-table-season-select"
      />
      {resultsCompetitions.some((c) => c.id === competition) && (
        <button type="button" className="module-switch-btn" onClick={onResults}>
          Results
        </button>
      )}
    </SubControls>
  );

  if (!table || !table.table.length) {
    const seasonPart = table?.seasonCode ? ` in ${humanizeSeasonCode(table.seasonCode)}` : "";
    return (
      <>
        {subControls}
        <div className="stats-empty">
          No table data captured for {competitionLabel(competitions, competition)}
          {seasonPart}.
        </div>
      </>
    );
  }

  return (
    <>
      {LIVE && (
        <ModuleExplainer
          mod={mod}
          paragraphs={explainLeagueTable({
            competition: competitionLabel(competitions, competition),
            season: humanizeSeasonCode(table.seasonCode),
          })}
        />
      )}
      {subControls}
      <div className="data-wrapper top-align" ref={containerRef}>
        <div className="data-row header">
          <div className="col-rank" onClick={rankProps.onClick}>#</div>
          <div className="col-team no-sort">Team</div>
          <div className={`col-num ${headerProps(1).className}`.trim()} onClick={headerProps(1).onClick}>Played</div>
          <div className={`col-num ${headerProps(2).className}`.trim()} onClick={headerProps(2).onClick}>W</div>
          <div className={`col-num ${headerProps(3).className}`.trim()} onClick={headerProps(3).onClick}>D</div>
          <div className={`col-num ${headerProps(4).className}`.trim()} onClick={headerProps(4).onClick}>L</div>
          <div className={`col-num ${headerProps(5).className}`.trim()} onClick={headerProps(5).onClick}>GF</div>
          <div className={`col-num ${headerProps(6).className}`.trim()} onClick={headerProps(6).onClick}>GA</div>
          <div className={`col-num ${headerProps(7).className}`.trim()} onClick={headerProps(7).onClick}>GD</div>
          <div className={`col-num pts ${headerProps(8).className}`.trim()} onClick={headerProps(8).onClick}>Pts</div>
          {LIVE && <div className="col-form no-sort">Form</div>}
        </div>
        {sortedTable.map((team, i) => (
          <div
            key={team.name}
            data-sort-key={team.name}
            className={`data-row league-table-row-clickable${team.teamId !== undefined && team.teamId === highlight ? " highlighted" : ""}`}
            onClick={() => onRowClick(team)}
            onPointerDown={(e) => onRowPress(e, team)}
          >
            <div className="col-rank">{i + 1}</div>
            <div className="col-team">{team.name}</div>
            <div className="col-num">{team.played}</div>
            <div className="col-num">{team.won}</div>
            <div className="col-num">{team.drawn}</div>
            <div className="col-num">{team.lost}</div>
            <div className="col-num">{team.goalsFor}</div>
            <div className="col-num">{team.goalsAgainst}</div>
            <div className="col-num">{team.goalDifference}</div>
            <div className="col-num pts">{team.points}</div>
            {LIVE && (
              <div className="col-form">
                {teamForm(fixtures, team.name).map((g, k) => (
                  <FormDot key={k} game={g} competitionId={competition} season={table?.seasonCode ?? null} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
